import { Add, Edit } from "@mui/icons-material";
import {
    Button,
    IconButton,
    Switch,
    Tooltip
} from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomDialog from "../../Shared/CustomDialogBox";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";

const DesignationMaster = () => {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);
    const initialValue = {
        search: '',
        start_date: '',
        end_date: '',
    };

    const formik = useFormik({
        initialValues: initialValue,
        enableReinitialize: true,
    });

    const { loading, data: designationList } = useQuery(
        ["get_designation_master", page, formik.values.search, formik.values.start_date, formik.values.end_date],
        () => {
            return axiosInstance.get(API_URLS.get_designation_master, {
                params: {
                    search: formik.values.search,
                    start_date: formik.values.start_date,
                    end_date: formik.values.end_date,
                    page: page,
                    count: 10,
                },
            });
        },
        {
            refetchOnMount: true,
            refetchOnReconnect: true,
            refetchOnWindowFocus: true

        }
    );

    const designation = designationList?.data?.response || [];

    const fk = useFormik({
        initialValues: {
            name: "",
            desc: "",
        },
        onSubmit: async (values) => {
            if (editData) return await updateDesignation(values);
            else return await createDesignation(values);
        },
    });

    const createDesignation = async (values) => {
        try {
            const reqBody = {
                m02_desig_name: values.name,
                m02_description: values.desc,
                m02_status: 1,
            };
            const res = await axiosInstance.post(API_URLS.create_designation, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_designation_master");
                handleClose();
            }
        } catch (e) {
            toast.error("Something went wrong while creating");
        }
    };

    const updateDesignation = async (values) => {
        try {
            const reqBody = {
                m02_desig_id: editData.m02_desig_id,
                m02_desig_name: values.name,
                m02_desig_slug: values.slug,
                m02_description: values.desc,
                m02_status: String(editData?.m02_status)
            };
            const res = await axiosInstance.post(API_URLS.update_designation, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_designation_master");
                handleClose();
            }
        } catch (e) {
            toast.error("Something went wrong while updating");
        }
    };

    const toggleStatus = async (row, checked) => {
        try {
            const reqBody = {
                m02_desig_id: row.m02_desig_id,
                m02_status: checked ? "1" : "0"
            };
            const res = await axiosInstance.post(API_URLS.update_designation_status, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_designation_master");
            }
        } catch (e) {
            toast.error("Failed to update status");
        }
    };

     const handleExport = async () => {
        try {
          const res = await axiosInstance.get(API_URLS.designation_master_excel, {
            search: formik.values.search || "",
            start_date: formik.values.start_date || "",
            end_date: formik.values.end_date || "",
          })
          const fileUrl = res?.data?.response?.url;
          window.open(fileUrl, "_blank")
        }
        catch (e) {
          console.log("Something went wrong")
        }
      }

    const handleOpen = () => {
        fk.resetForm();
        setEditData(null);
        setOpen(true);
    };

    const handleEdit = (row) => {
        setEditData(row);
        fk.setValues({
            name: row.m02_desig_name,
            desc: row.m02_description,
        });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditData(null);
    };

    const tablehead = [
        "S.No",
        "Name",
        "Status",
        "Actions",
        "Date",
    ];

    const tablerow = designation?.data?.map((item, index) => [
        <span>{index + 1}</span>,
        <span>{item?.m02_desig_name}</span>,
        <Switch
            checked={item?.m02_status === 1}
            onChange={(e) => toggleStatus(item, e.target.checked)}
            color="success"
        />
        ,

        <div className="flex justify-center gap-3">
            <Tooltip title="Edit">
                <IconButton onClick={() => handleEdit(item)}>
                    <Edit className="text-blue-600" />
                </IconButton>
            </Tooltip>
        </div>,
        <span>{moment(item?.m04_created_at)?.format("DD-MM-YYYY")}</span>
    ]);

    return (
        <>
            <div className="mx-5">
                <div className="flex justify-between items-center p-4 ">
                    <p className="font-bold text-xl">Designation Master</p>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpen}
                        className="!bg-gradient-to-b from-[#7981F9] to-[#5E60D0]"
                    >
                        Add Designation
                    </Button>
                </div>

                <CustomFilter
                    formik={formik}
                    onFilter={() => {
                        setPage(1);
                        queryClient.invalidateQueries("get_designation_master");
                    }}
                    onClear={() => {
                        formik.resetForm();
                        setPage(1);
                        queryClient.invalidateQueries("get_designation_master");
                    }}
                    onExport={handleExport}

                />

                <CustomTable
                    tablehead={tablehead}
                    tablerow={tablerow}
                    isLoading={loading}
                />

                <CustomToPagination
                    setPage={setPage}
                    page={page}
                    data={designation}
                />

            </div>

            <CustomDialog
                open={open}
                onClose={() => setOpen(false)}
                onSubmit={fk.handleSubmit}
                title={editData ? "Edit Designation" : "Add Designation"}
                formik={fk}
                fields={[
                    { name: "name", label: "Name", type: "text" },
                    { name: "desc", label: "Description", type: "text" }
                ]}
            />



        </>
    );
};

export default DesignationMaster;

